import React, { lazy } from 'react';
import { motion } from 'framer-motion';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 48
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const images = {
  living: 'https://cdn.magicpatterns.com/uploads/ftxFs7wvjnHRavz7jmQFGF/10.png',
  dining: 'https://cdn.magicpatterns.com/uploads/u64xa7nfqixxQJoS4wgcBN/11.png',
  kitchen: 'https://cdn.magicpatterns.com/uploads/cR4hKEkprNwNU5E88h8xtx/12.png',
  lounge: 'https://cdn.magicpatterns.com/uploads/4AJa3SieiyEfhsDZLRYnTS/13.png',
  bedroom: 'https://cdn.magicpatterns.com/uploads/9SVdvNg6Ny3TJz11ugJFf7/14.png',
  bath: 'https://cdn.magicpatterns.com/uploads/ogep1UmqLMut3eavzDoDrL/15.png',
  foyer: 'https://cdn.magicpatterns.com/uploads/uGzDvvMDVkdyHFFmjCcJzc/WhatsApp_Image_2026-10-03_at_16.17.33.jpg',
  upperLiving: 'https://cdn.magicpatterns.com/uploads/rrqEuCYQeGJcEN9mWZLzmi/WhatsApp_Image_2026-10-03_at_16.17.39_(2).jpg'
};
interface FloatImageProps {
  src: string;
  alt: string;
  className?: string;
  ratio?: string;
}
function FloatImage({
  src,
  alt,
  className = '',
  ratio = 'aspect-[16/10]'
}: FloatImageProps) {
  return <motion.figure variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-90px'
  }} className={`group overflow-hidden ${className}`} data-id="element-37" data-mp="1qdayg090mhy0">
      <img src={src} alt={alt} loading="lazy" className={`w-full h-full object-cover ${ratio} transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]`} data-id="element-38" data-mp="1ha9vg8fg56yk" />
    </motion.figure>;
}
function FloatText({
  eyebrow,
  children,
  className = ''
}: {
  eyebrow: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-90px'
  }} className={className} data-id="element-39" data-mp="31sr7cweeu5m1">
      <p className="text-[0.7rem] tracking-[0.28em] uppercase text-text-muted font-light mb-3" data-id="element-40" data-mp="35anfytyokrb1">
        {eyebrow}
      </p>
      <p className="font-heading text-xl md:text-2xl font-light leading-snug text-primary" data-id="element-41" data-mp="3097punhnbuls">
        {children}
      </p>
    </motion.div>;
}
export function FeatureGallery() {
  return <section className="py-16 md:py-32 px-6 md:px-14 bg-bg-warm overflow-hidden relative z-10" data-id="element-42" data-mp="2rbfa5i80ikxv">
      <div className="max-w-[1400px] mx-auto" data-id="element-43" data-mp="3nzllenzfwbl4">
        {/* Section intro */}
        <motion.header variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} className="mb-14 md:mb-24 max-w-2xl" data-id="element-44" data-mp="3j21w8t17eeee">
          <p className="text-xs tracking-[0.28em] text-text-muted font-light mb-4 uppercase" data-id="element-45" data-mp="3a6vwaaom6qjs">
            Thoughtful design
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-primary leading-tight" data-id="element-46" data-mp="5u8iwum3mq4z">
            Designed around light, space, and the way you live
          </h2>
        </motion.header>

        {/* Dispersed, floating composition */}
        <div className="flex flex-col gap-6 md:gap-0" data-id="element-47" data-mp="1xhwulbm7i4bd">
          {/* Block 1 — wide living image pulled right, text floating left */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:items-end" data-id="element-48" data-mp="22h9qelv22lq3">
            <FloatText eyebrow="Spacious interior" className="hidden md:block md:col-span-3 md:pb-6" data-id="element-49" data-mp="27gckxcdet9jt">
              Rooms that breathe, with an open plan that flows from one moment
              to the next.
            </FloatText>
            <FloatImage src={images.living} alt="Sunlit living room with floor-to-ceiling windows" className="md:col-span-9" ratio="aspect-[16/10]" data-id="element-50" data-mp="2h80h1qkwverm" />
          </div>

          {/* Block 2 — dining image left, text floating right */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:mt-28 md:items-center" data-id="element-51" data-mp="2v3cdq3imnfb6">
            <FloatImage src={images.dining} alt="Dining area opening onto greenery" className="md:col-span-7" ratio="aspect-[16/10]" data-id="element-52" data-mp="2hh565vczui0z" />
            <FloatText eyebrow="Considered design" className="hidden md:block md:col-span-4 md:col-start-9" data-id="element-53" data-mp="2rauf6xwq7ej0">
              Every sightline framed toward the garden and the light beyond.
            </FloatText>
          </div>

          {/* Block 3 — full-bleed kitchen band */}
          <FloatImage src={images.kitchen} alt="Chef's kitchen with warm timber cabinetry" className="md:mt-28" ratio="aspect-[16/9] md:aspect-[24/9]" data-id="element-54" data-mp="28ia1inpllfzs" />

          {/* Block 4 — two offset images at different heights */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-10 md:mt-28 md:items-start" data-id="element-55" data-mp="3jzjldbcn2a1m">
            <FloatImage src={images.lounge} alt="Bright lounge with garden views" className="md:col-span-5 md:mt-24" ratio="aspect-[16/10]" data-id="element-56" data-mp="3f0gdlj59xppy" />
            <div className="hidden md:block md:col-span-1" data-id="element-57" data-mp="3reljaoq1uxxx" />
            <FloatImage src={images.bedroom} alt="Primary bedroom framing the treetops" className="md:col-span-6" ratio="aspect-[16/11]" data-id="element-58" data-mp="3dihz7o8w45z6" />
          </div>

          {/* Block 5 — bathroom image left, text floating right */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:mt-28 md:items-center" data-id="element-59" data-mp="e0qi5kin9f3a">
            <FloatImage src={images.bath} alt="Spa-like bathroom with timber vanity and walk-in shower" className="md:col-span-8" ratio="aspect-[16/9]" data-id="element-60" data-mp="9lpx3hlvp2z" />
            <FloatText eyebrow="Everyday calm" className="hidden md:block md:col-span-3 md:col-start-10" data-id="element-61" data-mp="s09qoefdasg4">
              A retreat for the small rituals, wrapped in warm light and stone.
            </FloatText>
          </div>

          {/* Block 6 — actual photos: portrait foyer + wide upper living, offset */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-10 md:mt-28 md:items-start" data-id="element-62" data-mp="xnledgy0w4ym">
            <FloatImage src={images.foyer} alt="Entry foyer with sculptural timber-clad spiral staircase" className="md:col-span-4" ratio="aspect-[3/4]" data-id="element-63" data-mp="spa1sygcfgcp" />
            <FloatImage src={images.upperLiving} alt="Upper-level living space wrapped in floor-to-ceiling glass and treetop views" className="md:col-span-8 md:mt-32" ratio="aspect-[4/3]" data-id="element-64" data-mp="ixw6l2vfd2pd" />
          </div>
        </div>
      </div>
    </section>;
}